import type { ReactNode } from 'react';
import { Button } from './Button';

/**
 * BulkActionsBar — 「N 件選択中」+ 一括 action button 群を sticky bottom で表示
 * (Sprint BG-DataSettings 21、design system 補完)。
 *
 * 旧 v1 では PostsModerationPage で「全選択 (N/M) + bulk hide/delete button」を
 * 一覧の上に inline 固定で描画していた (old/apps/admin/.../PostsModerationPage.tsx L412-433)。
 * v2 では shared-ui で再利用可能 component 化し、sticky bottom + clear button + count badge を統一。
 *
 * 設計:
 *   - selectedCount === 0 のときは render しない (親側で常に呼んで OK)
 *   - sticky bottom-0 で画面下に常に表示 (mobile では bottom safe area を意識)
 *   - actions は ButtonProps 風 ({ label, onClick, variant, disabled }) で配列
 *   - clear callback で 「選択解除」 link button を表示
 *
 * a11y:
 *   - role="region" + aria-label="一括操作" — landmark
 *   - aria-live="polite" で N 件選択中の変化を SR 通知
 */

export interface BulkAction {
  /** key 用一意 id (例 'hide' / 'delete')。 */
  id: string;
  label: ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

export interface BulkActionsBarProps {
  selectedCount: number;
  /** action button 群 (左から右に並ぶ)。 */
  actions: BulkAction[];
  /** 「選択解除」 callback。 */
  onClear?: () => void;
  /** 処理中表示 (button 群を disable + 'spinner-like' 表示)。default false。 */
  busy?: boolean;
  /** sticky bottom にするか。default true。test/non-overlay で off に。 */
  sticky?: boolean;
  /** 「N 件選択中」の name (例 「投稿」「ユーザー」)。default '件'。 */
  itemLabel?: string;
  className?: string;
}

export function BulkActionsBar({
  selectedCount,
  actions,
  onClear,
  busy = false,
  sticky = true,
  itemLabel = '件',
  className = '',
}: BulkActionsBarProps) {
  if (selectedCount <= 0) return null;
  const stickyClass = sticky ? 'sticky bottom-0 z-20' : '';
  return (
    <div
      role="region"
      aria-label="一括操作"
      data-testid="bulk-actions-bar"
      className={`${stickyClass} flex flex-wrap items-center gap-2 bg-white border border-slate-300 shadow-md rounded-md px-3 py-2 ${className}`.trim()}
    >
      <span aria-live="polite" className="text-sm font-medium text-slate-900">
        {selectedCount} {itemLabel}選択中
      </span>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          aria-label="選択を解除"
          className="text-xs text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          選択解除
        </button>
      )}
      <span className="ml-auto flex flex-wrap items-center gap-2">
        {busy && (
          <span
            data-testid="bulk-actions-busy"
            className="text-xs text-slate-500"
            aria-live="polite"
          >
            処理中…
          </span>
        )}
        {actions.map((a) => (
          <Button
            key={a.id}
            size="sm"
            variant={a.variant ?? 'secondary'}
            onClick={a.onClick}
            disabled={a.disabled || busy}
          >
            {a.label}
          </Button>
        ))}
      </span>
    </div>
  );
}
